import { useEffect, useMemo, useRef, useState } from 'react'

import * as S from './styles'
import Tab from './Tab'

interface TabType {
  key: string
  icon: string
  iconClass?: string
  title: string
  content: React.ReactNode
}

interface TabsProps {
  defaultActiveTabIndex?: number
  isShowingTabSelector?: boolean
  tabs: TabType[]
}

export default function Tabs({
  defaultActiveTabIndex,
  isShowingTabSelector,
  tabs
}: TabsProps) {
  const tabComponentsAmount = tabs.length

  const [activeTabIndex, setActiveTabIndex] = useState(
    defaultActiveTabIndex ?? 0
  )
  const [activeTabIndicatorStyles, setActiveTabIndicatorStyles] = useState({
    transform: 'translateX(0)',
    width: 0
  })

  const buttonsContainerRef = useRef(null)

  const activeTabComponent = useMemo(
    () => tabs[activeTabIndex].content,
    [tabs, activeTabIndex]
  )

  useEffect(() => {
    function updateActiveTabIndicatorStyles() {
      if (!buttonsContainerRef.current) return

      const containerWidth = buttonsContainerRef.current.clientWidth

      const indicatorWidth = containerWidth / tabComponentsAmount
      const indicatorTranslation = indicatorWidth * activeTabIndex

      setActiveTabIndicatorStyles({
        transform: `translateX(${indicatorTranslation}px)`,
        width: indicatorWidth
      })
    }

    updateActiveTabIndicatorStyles()

    const controller = new AbortController()

    window.addEventListener(
      'resize',
      () => {
        updateActiveTabIndicatorStyles()
      },
      { signal: controller.signal }
    )

    return () => {
      controller.abort()
    }
  }, [activeTabIndex, tabComponentsAmount])

  return (
    <S.Container>
      {isShowingTabSelector && (
        <S.TabButtonsContainer ref={buttonsContainerRef}>
          {tabs.map((tab, index) => (
            <li key={tab.key} id={`tab-btn-${tab.key}`}>
              <S.TabButton
                className={index === activeTabIndex ? 'active' : ''}
                onClick={setActiveTabIndex.bind(null, index)}
              >
                <span className={tab.iconClass}>{tab.icon}</span>
                <span>{tab.title}</span>
              </S.TabButton>
            </li>
          ))}
          <S.ActiveTabIndicator style={activeTabIndicatorStyles} />
        </S.TabButtonsContainer>
      )}
      {activeTabComponent}
    </S.Container>
  )
}

Tabs.Tab = Tab
